import React, { useState } from 'react';
import './CreatePuzzleGift.css';
import InteractiveCard from './InteractiveCard';
import { uploadImageFromGift } from '../utils/uploadImageFromGift'; // Adjust path as needed
import { generateImageFromPrompt } from '../utils/generateImageFromPrompt';


const cardTypes = [
  'Invitation',
  'Greeting Card',
  'Anniversary',
  'Holiday',
  'Coupon/Offer',
];

const CreatePuzzleGift = () => {
  const [cardType, setCardType] = useState('');
  const [formData, setFormData] = useState({
    toName: '',
    toEmail: '',
    fromName: '',
    fromEmail: '',
    occasion: '',
    eventDate: '',
    imagePrompt: '',
    customMessage: '',
    tone: '',
    offerDescription: '',
    termsConditions: '',
    startDate: '',
    expirationDate: '',
    businessName: '',
    businessLocation: ''
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const enhancePromptWithTone = (basePrompt, tone) => {
  const toneStyles = {
    playful: "in a colorful cartoon style, cheerful lighting, fun and whimsical",
    dramatic: "high contrast, cinematic lighting, bold shadows, intense atmosphere",
    romantic: "soft lighting, pastel tones, dreamy and elegant, watercolor style",
    formal: "minimalist style, clean lines, elegant composition, neutral palette",
    heartfelt: "warm lighting, emotional scene, soft textures, gentle glow",
  };

  const style = toneStyles[tone.toLowerCase()] || "";
  return `${basePrompt}. Style: ${style}`;
};

  const buildAIPrompt = () => {
  const {
    toName, fromName, occasion, eventDate, imagePrompt, customMessage, tone,
    offerDescription, termsConditions, startDate, expirationDate, businessName, businessLocation,
  } = formData;

  const enhancedPrompt = enhancePromptWithTone(imagePrompt, tone);

  let prompt = `Create a ${cardType} card for the following:\n`;
  prompt += `Occasion: ${occasion || cardType}\n`;
  prompt += `Recipient: ${toName}\n`;
  prompt += `From: ${fromName}\n`;
  prompt += `Tone: ${tone}\n`;
  prompt += `Image Description: ${enhancedPrompt}\n`; 
  prompt += `Custom Message: ${customMessage}\n`;
  if (eventDate) prompt += `Event Date: ${eventDate}\n`;

  if (cardType === 'Coupon/Offer') {
    prompt += `\n--- Coupon Details ---\n`;
    prompt += `Offer: ${offerDescription}\n`;
    prompt += `Terms: ${termsConditions}\n`;
    prompt += `Start Date: ${startDate}\n`;
    prompt += `Expiration Date: ${expirationDate}\n`;
    prompt += `Business Name: ${businessName}\n`;
    prompt += `Location: ${businessLocation}\n`;
    prompt += `\nDisplay the terms, business name, location, and offer on the back side of the card.`;
  }

  return prompt;
};

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsGenerating(true);
  setErrorMessage('');
  const prompt = buildAIPrompt();
  console.log('🧠 Sending AI prompt:', prompt);

  try {
    const base64Image = await generateImageFromPrompt(prompt);
    const fileName = `puzzle_${Date.now()}.png`;

    const imageUrl = await uploadImageFromGift(base64Image, fileName);
    console.log('✅ Image uploaded:', imageUrl);
    setPreviewImage(imageUrl);
  } catch (err) {
    console.error(err);
    setErrorMessage('❌ Something went wrong while generating or uploading the image.');
  } finally {
    setIsGenerating(false);
  }
};

  return (
    <main className="create-puzzle-main">
      <h1>Create Your Puzzle Gift</h1>

      <section className="instructions">
        <h2>Steps to Create Your Puzzle Card</h2>
        <ol>
          <li>Choose your card type.</li>
          <li>Fill in recipient and event details.</li>
          <li>Describe your card image and tone.</li>
          <li>Submit to generate a unique puzzle card.</li>
        </ol>
      </section>

      <section className="card-form">
        <h3>Choose Card Type:</h3>
        <div className="card-type-options">
          {cardTypes.map((type) => (
            <label key={type}>
              <input
                type="radio"
                name="cardType"
                value={type}
                checked={cardType === type}
                onChange={(e) => setCardType(e.target.value)}
              />
              {type}
            </label>
          ))}
        </div>

        {cardType && (
          <form className="puzzle-gift-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Recipient's Name</label>
              <input type="text" name="toName" value={formData.toName} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Recipient's Email</label>
              <input type="email" name="toEmail" value={formData.toEmail} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Your Name</label>
              <input type="text" name="fromName" value={formData.fromName} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Your Email</label>
              <input type="email" name="fromEmail" value={formData.fromEmail} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Occasion or Event</label>
              <input type="text" name="occasion" value={formData.occasion} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Event Date (optional)</label>
              <input type="date" name="eventDate" value={formData.eventDate} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Describe the Image (AI Prompt: The more details you provide, the better your image will be.)</label>
              <textarea name="imagePrompt" rows="3" value={formData.imagePrompt} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Personal Message</label>
              <textarea name="customMessage" rows="3" value={formData.customMessage} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label>Tone (e.g., playful, heartfelt, formal)</label>
              <input type="text" name="tone" value={formData.tone} onChange={handleChange} />
            </div>

            {cardType === 'Coupon/Offer' && (
              <>
                <div className="form-group">
                  <label>Offer Description</label>
                  <input type="text" name="offerDescription" value={formData.offerDescription} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Terms & Conditions</label>
                  <textarea name="termsConditions" rows="3" value={formData.termsConditions} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Expiration Date</label>
                  <input type="date" name="expirationDate" value={formData.expirationDate} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Business Name</label>
                  <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Location Address</label>
                  <input type="text" name="businessLocation" value={formData.businessLocation} onChange={handleChange} />
                </div>
              </>
            )}

            <button type="submit" disabled={isGenerating}>
              {isGenerating
                ? 'Generating Puzzle Card…'
                : previewImage
                  ? 'Build New Image'
                  : 'Generate Puzzle Card'}
            </button>

            {/* Generate Message to let user know that system is processing */}
            {isGenerating && (
              <p style={{ color: 'blue', marginTop: '1rem' }}>
              🎨 Please wait while your image is being generated...
            </p>
)}
            {errorMessage && (
              <p style={{ color: 'red', marginTop: '1rem' }}>{errorMessage}</p>
)}
          </form>
        )}

        {previewImage && (
          <>
            <h3 style={{ textAlign: 'center', marginTop: '2rem' }}>Preview Your Puzzle Card</h3>
            <InteractiveCard
              image={previewImage}
              message={formData.customMessage}
              cardType={cardType}
              offerDescription={formData.offerDescription}
              termsConditions={formData.termsConditions}
              businessName={formData.businessName}
              businessLocation={formData.businessLocation}
            />
          </>
        )}
      </section>
    </main>
  );
};

export default CreatePuzzleGift;
